import { Button as MUIButton, Box, styled } from "@mui/material";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";

import { ButtonProps } from "./Button.types";

export const Container = styled(MUIButton)<ButtonProps>(
  ({ size, color, variant, loading, theme: { palette } }) => {
    const { grey } = palette;
    let styleBasedOnType = {};
    const isOutlined = variant === "outlined";
    const sizeOverrides = size as ButtonProps["size"];
    const defaultIconSizeStyle = {
      minWidth: 0,
      minHeight: 0,
    };
    switch (sizeOverrides) {
      case "sm": {
        styleBasedOnType = { padding: isOutlined ? "8px 14px" : "8px 14px" };
        break;
      }
      case "md": {
        styleBasedOnType = { padding: isOutlined ? "9px 15px" : "10px 16px" };
        break;
      }
      case "lg": {
        styleBasedOnType = { padding: isOutlined ? "9px 17px" : "10px 18px" };
        break;
      }
      case "xl": {
        styleBasedOnType = { padding: isOutlined ? "11px 19px" : "12px 20px" };
        break;
      }
      case "2xl": {
        styleBasedOnType = { padding: isOutlined ? "15px 27px" : "16px 28px" };
        break;
      }
      case "icon-sm": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 8px
           * Adjustment: 7px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? "7px" : "8px",
        };
        break;
      }
      case "icon-md": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 10px
           * Adjustment: 9px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? "9px" : "10px",
        };
        break;
      }
      case "icon-lg": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 12px
           * Adjustment: 11px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? "11px" : "12px",
        };
        break;
      }
      case "icon-xl": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 14px
           * Adjustment: 13px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? "13px" : "14px",
        };
        break;
      }
      case "icon-2xl": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 16px
           * Adjustment: 15px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? "15px" : "16px",
        };
        break;
      }
      default:
        break;
    }
    const boxShadowPaletteColor = color
      ? ((color === "inherit" ? grey : palette[color]) as unknown as Record<
          number,
          string
        >)
      : null;

    return {
      position: "relative",
      ...styleBasedOnType,
      lineHeight: "20px",
      padding: "8px 14px",

      // Override "box-shadow"
      "&:not(.MuiButton-text):not(.MuiButton-disableElevation)": {
        "&, &:hover": {
          boxShadow: shadows.xs,
        },
        "&:active": {
          boxShadow: boxShadowPaletteColor
            ? `0px 0px 0px 4px ${boxShadowPaletteColor[100]}, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)`
            : undefined,
        },
      },

      // Override "outlined" variant and "inherit" color
      "&:not(.Mui-disabled).MuiButton-outlinedInherit": {
        color: gray[700],
        borderColor: gray[300],
      },

      ".MuiButton-startIcon, .MuiButton-endIcon": {
        opacity: loading ? 0 : 1,
        transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      },
    };
  }
);

export const LoadingWrapper = styled(Box)(() => ({
  display: "inline-flex",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
}));

export const ContentWrapper = styled(Box)<Pick<ButtonProps, "loading">>(
  ({ loading }) => ({
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: loading ? 0 : 1,
    transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

    "& > *": {
      color: "inherit",
    },
  })
);
